import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { appName, supportEmail, copyright } from '../config/branding';

const EFFECTIVE_DATE = '5 May 2026';
const LEGAL_ENTITY = 'A-Professional (Pty) Ltd';
const REGISTERED_ADDRESS = '7 Fernwood Drive, Fernwood Estate, Somerset West, 7140, South Africa';
const CIPC_NUMBER = '2026/359204/07';

const LegalPage = ({ title, children }) => (
  <div className="min-h-screen bg-gray-50">
    <header className="bg-white border-b border-gray-200">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
        <Link to="/" className="text-2xl font-bold text-[#37b34a]" data-testid="legal-home-link">
          {appName}
        </Link>
        <Link
          to="/"
          className="text-sm text-gray-500 hover:text-gray-900 font-medium flex items-center gap-1.5 transition-colors"
        >
          <ArrowLeft size={14} /> Back to home
        </Link>
      </div>
    </header>
    <main className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
      <h1 className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-2">{title}</h1>
      <p className="text-sm text-gray-500 mb-8">Effective date: {EFFECTIVE_DATE}</p>
      <article className="max-w-none text-gray-700 leading-relaxed text-sm sm:text-base space-y-4 [&_h2]:text-xl [&_h2]:font-bold [&_h2]:text-gray-900 [&_h2]:mt-8 [&_h2]:mb-2 [&_p]:my-3 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:my-3 [&_ul]:space-y-1.5 [&_li]:text-gray-700 [&_a]:text-[#37b34a] hover:[&_a]:underline [&_strong]:text-gray-900 [&_strong]:font-semibold">
        {children}
      </article>
      <footer className="mt-12 pt-6 border-t border-gray-200 text-xs text-gray-500">
        {copyright} · <Link to="/terms" className="hover:text-gray-700">Terms</Link> ·{' '}
        <Link to="/privacy" className="hover:text-gray-700">Privacy</Link>
      </footer>
    </main>
  </div>
);

export const TermsOfService = () => (
  <LegalPage title="Terms of Service">
    <p>
      These Terms of Service ("<strong>Terms</strong>") form a binding agreement between you and{' '}
      <strong>{LEGAL_ENTITY}</strong> ("<strong>{appName}</strong>", "we", "us", "our"), the operator of the {appName}{' '}
      platform ("<strong>Platform</strong>"). By creating an account or using the Platform you confirm that you accept
      these Terms. If you do not accept them, do not use the Platform.
    </p>

    <h2>1. Eligibility</h2>
    <p>
      You must be at least 18 years old and legally able to enter into a contract under South African law. If you act on
      behalf of a business, you confirm that you have authority to bind that business.
    </p>

    <h2>2. Accounts and registration</h2>
    <ul>
      <li>You must provide accurate, current and complete information.</li>
      <li>You are responsible for safeguarding your sign-in credentials and for all activity on your account.</li>
      <li>We may verify your identity, contact details and credentials at any time and may suspend access pending verification.</li>
    </ul>

    <h2>3. Customer obligations</h2>
    <p>
      When you submit a service request as a customer you agree that the information you provide is accurate, that you
      genuinely require the service, and that the contact details you supply belong to you and may lawfully be shared
      with matched professionals who unlock the lead.
    </p>

    <h2>4. Professional obligations</h2>
    <ul>
      <li>You will only offer services you are competent and (where applicable) licensed to provide.</li>
      <li>You will respond to unlocked leads in good faith and provide accurate quotes.</li>
      <li>You will not contact customers for purposes unrelated to the service requested.</li>
      <li>We may suspend or terminate your account for repeated complaints, fraudulent activity, or breach of these Terms.</li>
    </ul>

    <h2>5. Credits, payments and refunds</h2>
    <p>
      Professionals unlock customer leads using credits. Credits are sold in fixed packages and processed by Stripe
      (Stripe Payments Europe Limited / its applicable affiliate). Prices are shown in South African Rand (ZAR) inclusive
      of any applicable taxes unless stated otherwise.
    </p>
    <ul>
      <li><strong>Free credits</strong> granted on registration expire 7 days after issue and are limited to a maximum of 5 lead unlocks.</li>
      <li><strong>Paid credits</strong> do not expire and are non-transferable.</li>
      <li>Credit purchases are final once the underlying lead has been unlocked, except as set out in our Lead Issue policy below.</li>
    </ul>

    <h2>6. Lead issue reports and refunds</h2>
    <p>
      If a lead you unlocked is materially inaccurate, fraudulent, unreachable, or otherwise unfit (e.g. invalid phone,
      duplicate, wrong service, spam), you may submit a Lead Issue Report from your dashboard. If we approve the report,
      the credits used to unlock that lead are refunded to your wallet. We may decline reports that appear vexatious,
      contradicted by our records, or submitted in bad faith.
    </p>

    <h2>7. Soft launch and account activation</h2>
    <p>
      During our soft-launch period, lead-unlocking is enabled only after a manual activation step. Your account may
      remain in a "pending verification" state until our team confirms eligibility. We make no guarantee that any specific
      lead volume, quality, or revenue will be available to any professional.
    </p>

    <h2>8. Prohibited conduct</h2>
    <ul>
      <li>Misrepresenting your identity, credentials or services.</li>
      <li>Harvesting or scraping customer or professional data.</li>
      <li>Using the Platform to send unsolicited marketing.</li>
      <li>Circumventing the credit-based unlocking system or resale of leads.</li>
      <li>Posting unlawful, threatening, defamatory, infringing, obscene, or otherwise objectionable content.</li>
    </ul>

    <h2>9. Intellectual property</h2>
    <p>
      All content, software, design and trademarks on the Platform belong to {LEGAL_ENTITY} or our licensors. Nothing in
      these Terms transfers any of those rights to you. You retain ownership of content you submit but grant us a
      non-exclusive, royalty-free licence to host, display and process it for the purpose of operating the Platform.
    </p>

    <h2>10. Limitation of liability</h2>
    <p>
      To the maximum extent permitted by South African law, {appName} is not liable for any indirect, incidental,
      consequential, special or punitive damages, or for loss of profits, revenue, data or goodwill arising from your use
      of the Platform. Our total liability for any claim shall not exceed the credit amount you paid in the 12 months
      preceding the event giving rise to the claim. Nothing in these Terms excludes liability that cannot be excluded
      under applicable law (including the Consumer Protection Act 68 of 2008).
    </p>

    <h2>11. Indemnity</h2>
    <p>
      You will indemnify and hold {appName} harmless from claims, losses or expenses (including reasonable legal fees)
      arising out of your breach of these Terms, your use of the Platform, or your interactions with customers or
      professionals matched through it.
    </p>

    <h2>12. Termination</h2>
    <p>
      You may stop using the Platform at any time. We may suspend or terminate your account for breach of these Terms,
      legal compliance, or to protect the safety of users. On termination, unused paid credits may be forfeited unless
      otherwise required by law.
    </p>

    <h2>13. Changes to these Terms</h2>
    <p>
      We may update these Terms from time to time. Material changes will be notified by email or in-app notice. Continued
      use of the Platform after changes take effect constitutes acceptance.
    </p>

    <h2>14. Governing law and dispute resolution</h2>
    <p>
      These Terms are governed by the laws of the Republic of South Africa. The courts of South Africa have exclusive
      jurisdiction over any dispute arising out of or in connection with them. Before initiating legal action, the parties
      will use reasonable efforts to resolve the dispute by good-faith discussion at <a href={`mailto:${supportEmail}`}>{supportEmail}</a>.
    </p>

    <h2>15. Contact</h2>
    <p>
      <strong>{LEGAL_ENTITY}</strong><br />
      {REGISTERED_ADDRESS}<br />
      Company registration: {CIPC_NUMBER}<br />
      Email: <a href={`mailto:${supportEmail}`}>{supportEmail}</a>
    </p>
  </LegalPage>
);

export const PrivacyPolicy = () => (
  <LegalPage title="Privacy Policy">
    <p>
      This Privacy Policy explains how <strong>{LEGAL_ENTITY}</strong> ("<strong>{appName}</strong>", "we", "us")
      collects, uses, shares and protects personal information when you use the {appName} Platform. We are committed to
      processing your personal information in accordance with the <strong>Protection of Personal Information Act 4 of
      2013 (POPIA)</strong> and the regulations issued under it.
    </p>

    <h2>1. Information Officer</h2>
    <p>
      Our Information Officer (as required by POPIA) can be contacted at:<br />
      Name: <strong>Raman Rajoo</strong><br />
      Email: <a href={`mailto:${supportEmail}`}>{supportEmail}</a><br />
      Postal address: {REGISTERED_ADDRESS}
    </p>

    <h2>2. Personal information we collect</h2>
    <ul>
      <li><strong>Account information:</strong> name, email, phone, profile picture (via Google sign-in or registration).</li>
      <li><strong>Professional information:</strong> business name, services, service areas, pricing, portfolio images, business description, certifications.</li>
      <li><strong>Customer requests:</strong> service category, location, budget, urgency, free-text descriptions, questionnaire answers.</li>
      <li><strong>Verification data:</strong> email and phone OTP codes (transient).</li>
      <li><strong>Transaction data:</strong> credit purchases, lead unlocks, payment metadata (Stripe handles card details — we never see them).</li>
      <li><strong>Technical data:</strong> IP address, device/browser type, pages viewed, cookies and similar identifiers.</li>
    </ul>

    <h2>3. Lawful basis for processing</h2>
    <p>We rely on one or more of the following grounds permitted by POPIA:</p>
    <ul>
      <li><strong>Contract:</strong> to provide the Platform and the services you request.</li>
      <li><strong>Consent:</strong> for non-essential cookies, marketing emails, and certain optional profile fields.</li>
      <li><strong>Legitimate interest:</strong> to prevent fraud, secure the Platform, and improve our services.</li>
      <li><strong>Legal obligation:</strong> to comply with tax, accounting, anti-money-laundering, and other applicable law.</li>
    </ul>

    <h2>4. How we use your information</h2>
    <ul>
      <li>To match customers with suitable professionals and to facilitate lead unlocking.</li>
      <li>To verify identity, prevent fraud, and enforce our Terms of Service.</li>
      <li>To process payments, issue receipts and maintain transaction records.</li>
      <li>To communicate with you (transactional emails such as receipts, lead-unlock notifications, and important account updates).</li>
      <li>To improve the Platform, debug issues, and develop new features.</li>
    </ul>

    <h2>5. Sharing your information</h2>
    <p>
      We do <strong>not</strong> sell your personal information. We share it only in the following circumstances:
    </p>
    <ul>
      <li><strong>Between matched users:</strong> when a professional unlocks your lead, your contact details are shared with that professional for the purpose of contacting you.</li>
      <li><strong>Operators (POPIA-defined):</strong> trusted third-party service providers acting on our instructions:
        <ul>
          <li><strong>Stripe</strong> — payment processing.</li>
          <li><strong>Resend</strong> — transactional email delivery.</li>
          <li><strong>Emergent</strong> — Google sign-in identity exchange.</li>
          <li><strong>Google</strong> — sign-in / OAuth authentication.</li>
          <li>Cloud infrastructure providers used to host the Platform.</li>
        </ul>
      </li>
      <li><strong>Legal requirements:</strong> to comply with a subpoena, court order or other legal obligation.</li>
      <li><strong>Business transfers:</strong> as part of a merger, acquisition or sale of assets, with prior notice where required.</li>
    </ul>

    <h2>6. Cross-border transfers</h2>
    <p>
      Some of our service providers may process personal information outside South Africa (e.g. in the European Union or
      the United States). We rely on the protections set out in section 72 of POPIA — including binding agreements
      requiring an adequate level of protection and your consent where applicable.
    </p>

    <h2>7. Retention</h2>
    <p>
      We retain personal information only for as long as necessary to fulfil the purposes set out in this Policy or to
      comply with our legal, accounting and reporting obligations. Transaction records are typically retained for at
      least 5 years in line with South African tax law. Inactive accounts may be anonymised after a reasonable period.
    </p>

    <h2>8. Your POPIA rights</h2>
    <p>You have the following rights, subject to certain exceptions:</p>
    <ul>
      <li>Access the personal information we hold about you.</li>
      <li>Request correction or deletion of inaccurate or excessive information.</li>
      <li>Object to or restrict certain processing.</li>
      <li>Withdraw consent for processing based on consent (this does not affect prior lawful processing).</li>
      <li>Lodge a complaint with the Information Regulator of South Africa (<a href="https://inforegulator.org.za" target="_blank" rel="noopener noreferrer">inforegulator.org.za</a>).</li>
    </ul>
    <p>
      To exercise any of these rights, contact our Information Officer at{' '}
      <a href={`mailto:${supportEmail}`}>{supportEmail}</a>. We will respond within a reasonable time and free of charge,
      except where the request is manifestly unfounded or excessive.
    </p>

    <h2>9. Cookies</h2>
    <p>
      We use cookies and similar technologies to keep you signed in, remember preferences, and measure how the Platform
      is used. You can manage your preferences via the cookie banner shown on first visit or by clearing your browser
      cookies. Strictly necessary cookies (sign-in, session, security) cannot be disabled because the Platform will not
      function without them.
    </p>

    <h2>10. Security</h2>
    <p>
      We use industry-standard safeguards including encryption in transit (HTTPS), restricted backend access, secure
      cookie flags, and webhook signature verification. No system is perfectly secure — please report any suspected
      breach to our Information Officer immediately.
    </p>

    <h2>11. Children</h2>
    <p>
      The Platform is not intended for children under 18. We do not knowingly collect personal information from minors.
      If you believe a child has provided us with personal information, contact us and we will delete it.
    </p>

    <h2>12. Changes to this Policy</h2>
    <p>
      We may update this Policy from time to time. The effective date at the top of the document indicates when it was
      last revised. Material changes will be notified by email or in-app notice.
    </p>

    <h2>13. Contact</h2>
    <p>
      <strong>{LEGAL_ENTITY}</strong><br />
      {REGISTERED_ADDRESS}<br />
      Company registration: {CIPC_NUMBER}<br />
      Email: <a href={`mailto:${supportEmail}`}>{supportEmail}</a>
    </p>
  </LegalPage>
);

// Default export not used; pages are imported individually.
export default LegalPage;
